import type {
  KafkaClusterWrapper,
  KafkaExtensionConfiguration,
} from '@/generated/http-clients/v3ApiSchemas'
import { NBSP } from '@/utils'
import { Code, Text } from '@chakra-ui/react'
import cloneDeep from 'lodash/cloneDeep'
import merge from 'lodash/merge'

const GENERIC_KAFKA_UI_SCHEMA = {
  $titleHide: true,
  'kafka-configuration': {
    $titleHide: true,
    'kafka-clusters': {
      $titleHide: true,
      'kafka-cluster': {
        $titleHide: true,
        'bootstrap-servers': {
          $label: 'Bootstrap Servers',
          $placeholder: 'Comma separated list of Kafka bootstrap servers',
          $description: (
            <>
              Insert the list of bootstrap server URLs of the Kafka cluster you
              want to connect your HiveMQ broker to. You can add a single server
              URL in the format <Code>host:port</Code> or add multiple server
              URLs as a comma separated list in the format{NBSP}
              <Code>host:port,host:port,…</Code>
            </>
          ),
        },
        authentication: {
          $title: 'Authentication',
          $description:
            'Add credentials to authenticate your HiveMQ Cloud Cluster with your Kafka bootstrap servers.',
          plain: {
            $titleHide: true,
            $title: 'Plain',
            username: {
              $label: 'Username',
              $placeholder: 'Kafka Username',
            },
            password: {
              $label: 'Password',
              $fieldType: 'password',
              $placeholder: 'Kafka Password',
            },
          },
        },
      },
    },
    'mqtt-to-kafka-mappings': {
      $titleHide: true,
      'mqtt-to-kafka-mapping': {
        $title: 'HiveMQ to Kafka Mapping',
        $description: (
          <>
            Define the MQTT topic from which you want to forward messages to
            your Kafka cluster. This integration forwards any message published
            with this MQTT topic to the Kafka topic specified in the destination
            topic input field.
            <br />
            <Text fontWeight={900} display="inline">
              Limitations:
            </Text>
            {NBSP}
            <Text fontWeight={700} display="inline">
              This integration supports throughput up to 250 MB/sec. Messages
              are sent with QoS 0 by default.
            </Text>
          </>
        ),

        'kafka-topic': {
          $label: 'To Kafka Topic (destination)',
          $placeholder: 'to.kafka.1',
        },
        'mqtt-topic-filters': {
          $titleHide: true,
          'mqtt-topic-filter': {
            $label: 'From MQTT Topic (source)',
            $placeholder: 'from/mqtt/1',
          },
        },
      },
    },
    'kafka-to-mqtt-mappings': {
      $titleHide: true,
      'kafka-to-mqtt-mapping': {
        $title: 'Kafka to HiveMQ Mapping',
        $description:
          'Define the Kafka topic from which you want to forward messages to your MQTT clients. This integration publishes each new message from your specified source topic in Kafka to your HiveMQ broker using the specified MQTT topic from the destination topic input field.',
        'kafka-topics': {
          $titleHide: true,
          'kafka-topic': {
            $label: 'From Kafka Topic (source)',
            $placeholder: 'from.kafka.2',
          },
        },
        'mqtt-topics': {
          $titleHide: true,
          'mqtt-topic': {
            $label: 'To MQTT Topic (destination)',
            $placeholder: 'to/mqtt/2',
          },
        },
      },
    },
  },
}

export enum KafkaFlavor {
  GENERIC = 'generic',
  CONFLUENT = 'confluent',
}

export enum KafkaClusterId {
  GENERIC = 'generic-kafka',
  CONFLUENT = 'confluent-kafka',
}

export function baseXMLKafkaV3Flavor(flavour: KafkaFlavor) {
  return `
<kafka-configuration>
  <kafka-clusters>
    <kafka-cluster>
      <id>${flavour}-kafka</id>
      <bootstrap-servers></bootstrap-servers>
      <authentication>
        <plain>
          <username></username>
          <password></password>
        </plain>
      </authentication>
      <tls>
        <enabled>true</enabled>
      </tls>
    </kafka-cluster>
  </kafka-clusters>
  <mqtt-to-kafka-mappings>
    <mqtt-to-kafka-mapping>
      <id>${flavour}-kafka__mqtt-to-kafka</id>
      <cluster-id>${flavour}-kafka</cluster-id>
      <kafka-topic></kafka-topic>
      <mqtt-topic-filters>
        <mqtt-topic-filter></mqtt-topic-filter>
      </mqtt-topic-filters>
    </mqtt-to-kafka-mapping>
  </mqtt-to-kafka-mappings>
  <kafka-to-mqtt-mappings>
    <kafka-to-mqtt-mapping>
      <id>${flavour}-kafka__kafka-to-mqtt</id>
      <cluster-id>${flavour}-kafka</cluster-id>
      <kafka-topics>
        <kafka-topic></kafka-topic>
      </kafka-topics>
      <mqtt-topics>
        <mqtt-topic></mqtt-topic>
      </mqtt-topics>
    </kafka-to-mqtt-mapping>
  </kafka-to-mqtt-mappings>
</kafka-configuration>
`
}

function mappingStructure(
  isMqttToKafkaRequired: boolean,
  isKafkaToMqttRequired: boolean,
) {
  const bothRequired = !isMqttToKafkaRequired && !isKafkaToMqttRequired

  return {
    'kafka-configuration': {
      'mqtt-to-kafka-mappings': {
        'mqtt-to-kafka-mapping': {
          'kafka-topic': {
            $isRequired: bothRequired || isMqttToKafkaRequired,
          },
          'mqtt-topic-filters': {
            'mqtt-topic-filter': {
              $isRequired: bothRequired || isMqttToKafkaRequired,
            },
          },
        },
      },
      'kafka-to-mqtt-mappings': {
        'kafka-to-mqtt-mapping': {
          'kafka-topics': {
            'kafka-topic': {
              $isRequired: bothRequired || isKafkaToMqttRequired,
            },
          },
          'mqtt-topics': {
            'mqtt-topic': {
              $isRequired: bothRequired || isKafkaToMqttRequired,
            },
          },
        },
      },
    },
  }
}

export function calculateUISchemaWithRequiredFields(
  // biome-ignore lint/suspicious/noExplicitAny: We cast some super dark magic here
  values: any,
  // biome-ignore lint/suspicious/noExplicitAny: We cast some super dark magic here
  UISchemaOverwrites: any,
) {
  const kafkaToMqttMapping =
    values['kafka-configuration']?.['kafka-to-mqtt-mappings']?.[
      'kafka-to-mqtt-mapping'
    ]

  const mqttToKafkaMapping =
    values['kafka-configuration']?.['mqtt-to-kafka-mappings']?.[
      'mqtt-to-kafka-mapping'
    ]

  const hasStartedFillingOutMqttToKafkaMapping =
    mqttToKafkaMapping?.['kafka-topic'] !== '' ||
    mqttToKafkaMapping?.['mqtt-topic-filters']?.['mqtt-topic-filter'] !== ''

  const hasStartedFillingOutKafkaToMqttMapping =
    kafkaToMqttMapping?.['kafka-topics']?.['kafka-topic'] !== '' ||
    kafkaToMqttMapping?.['mqtt-topics']?.['mqtt-topic'] !== ''

  return merge(
    {},
    GENERIC_KAFKA_UI_SCHEMA,
    mappingStructure(
      hasStartedFillingOutMqttToKafkaMapping,
      hasStartedFillingOutKafkaToMqttMapping,
    ),
    UISchemaOverwrites,
  )
}

const uiSchemaOverridesConfluent = {
  'kafka-configuration': {
    'kafka-clusters': {
      'kafka-cluster': {
        authentication: {
          $title: 'Authentication',
          $description:
            'Add your Kafka credentials to connect your HiveMQ broker securely to your Kafka Confluent cluster.',
          plain: {
            $titleHide: true,
            $title: 'Plain',
            username: {
              $label: 'API Key',
              $placeholder: 'Confluent API key',
            },
            password: {
              $label: 'API Secret',
              $fieldType: 'password',
              $placeholder: 'Confluent API secret',
            },
          },
        },
      },
    },
    'mqtt-to-kafka-mappings': {
      'mqtt-to-kafka-mapping': {
        $description: (
          <>
            Add a mapping to stream messages from your MQTT topics to your Kafka
            topic. You can use wildcards to match multiple MQTT topics. (
            <Code>+</Code> or <Code>#</Code>)
          </>
        ),
        $title: 'HiveMQ to Confluent Mapping',
      },
    },
    'kafka-to-mqtt-mappings': {
      $titleHide: true,
      'kafka-to-mqtt-mapping': {
        $title: 'Confluent to HiveMQ Mapping',
      },
    },
  },
}

const uiSchemaOverridesGeneric = {
  'kafka-configuration': {
    'kafka-clusters': {
      'kafka-cluster': {
        tls: {
          $titleHide: true,
        },
      },
    },
    'mqtt-to-kafka-mappings': {
      'mqtt-to-kafka-mapping': {
        $description: (
          <>
            Add a mapping to stream messages from your MQTT topics to your Kafka
            topic. You can use wildcards to match multiple MQTT topics. (
            <Code>+</Code> or <Code>#</Code>)
          </>
        ),
      },
    },
    'kafka-to-mqtt-mappings': {
      'kafka-to-mqtt-mapping': {
        $description: (
          <>
            You can use{NBSP}
            {/* biome-ignore lint/nursery/useConsistentCurlyBraces: curly braces needed to avoid KAFKA_TOPIC being considered a variable */}
            <Code>{'${KAFKA_TOPIC}'}</Code>,{NBSP}
            {/* biome-ignore lint/nursery/useConsistentCurlyBraces: curly braces needed to avoid KAFKA_KEY and KAFKA_VALUE being considered as variables */}
            <Code>{'${KAFKA_KEY}'}</Code>, or <Code>{'${KAFKA_VALUE}'}</Code>
            {NBSP} as variable part in the MQTT topic. This variable part will
            be replaced during runtime with the value from the Kafka record.
          </>
        ),
      },
    },
  },
}

// biome-ignore lint/suspicious/noExplicitAny: accepting any here
export function calculateUiSchemaKafka(jsonValues: any, flavor: KafkaFlavor) {
  const uiSchemaOverrides =
    flavor === KafkaFlavor.CONFLUENT
      ? uiSchemaOverridesConfluent
      : uiSchemaOverridesGeneric

  return calculateUISchemaWithRequiredFields(jsonValues, uiSchemaOverrides)
}

/**
 * KafkaExtensionConfigVariant is almost similar to KafkaExtensionConfiguration except that mqtt-to-kafka-mappings and kafka-to-mqtt-mappings are objects instead of arrays.
 * This is due to the way we transform JSON<->XML.
 * TODO Oli: remove xsd-forms for v3, and use json schema. Then this type can be removed.
 */
type KafkaExtensionConfigurationBase = NonNullable<
  KafkaExtensionConfiguration['kafka-configuration']
>
export type KafkaExtensionConfigVariant = {
  // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
  ['kafka-configuration']?: Omit<
    KafkaExtensionConfigurationBase,
    'kafka-to-mqtt-mappings' | 'mqtt-to-kafka-mappings' | 'kafka-clusters'
  > & {
    // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
    ['kafka-clusters']?: KafkaClusterWrapper
    // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
    ['kafka-to-mqtt-mappings']?: {
      // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
      ['kafka-to-mqtt-mapping']?: {
        id?: string
        // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
        ['cluster-id']?: string
        // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
        ['kafka-topics']?: {
          // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
          ['kafka-topic']?: string
        }
        // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
        ['mqtt-topics']?: {
          // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
          ['mqtt-topic']?: string
        }
      }
    }
    // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
    ['mqtt-to-kafka-mappings']?: {
      // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
      ['mqtt-to-kafka-mapping']?: {
        id?: string
        // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
        ['cluster-id']?: string
        // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
        ['mqtt-topic-filters']?: {
          // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
          ['mqtt-topic-filter']?: string
        }
        // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
        ['kafka-topic']?: string
      }
    }
  }
}

/**
 * When reading Kafka configuration from the GET /api/v3/orgs/{orgId}/clusters/{clusterId} API,
 * basic data (mapping ID, cluster ID) might be missing if a mapping is not
 * configured in one direction (e.g. kafka->mqtt or mqtt-kafka).
 * This function makes sure that all IDs are defined before the configuration
 * gets transformed into XML for xsd-forms.
 * @param {KafkaExtensionConfiguration} config Kafka configuration with potentially missing IDs
 * @returns {KafkaExtensionConfiguration} updated Kafka configuration
 */
export function prepareKafkaConfigurationFormData(
  config: NonNullable<KafkaExtensionConfiguration>,
): KafkaExtensionConfiguration {
  const updatedConfig: NonNullable<KafkaExtensionConfiguration> =
    cloneDeep(config)
  const kafkaConfig = updatedConfig['kafka-configuration']
  if (kafkaConfig) {
    const kafkaExtensionClusterID =
      kafkaConfig?.['kafka-clusters']?.[0]['kafka-cluster']?.id
    const kafkaIntegrationType = kafkaExtensionClusterID?.startsWith(
      KafkaFlavor.CONFLUENT,
    )
      ? KafkaFlavor.CONFLUENT
      : KafkaFlavor.GENERIC

    // if there's no kafka-to-mqtt-mapping, add one with an id and cluster-id specified
    const kafkaToMqttMappings = kafkaConfig['kafka-to-mqtt-mappings']
    if (!kafkaToMqttMappings?.length) {
      kafkaConfig['kafka-to-mqtt-mappings'] = [
        {
          'kafka-to-mqtt-mapping': {
            id: `${kafkaIntegrationType}-kafka__kafka-to-mqtt`,
            'cluster-id': kafkaExtensionClusterID,
          },
        },
      ]
    }

    // if there's no kafka-to-mqtt-mapping, add one with an id and cluster-id specified
    const mqttToKafkaMappings = kafkaConfig['mqtt-to-kafka-mappings']
    if (!mqttToKafkaMappings?.length) {
      kafkaConfig['mqtt-to-kafka-mappings'] = [
        {
          'mqtt-to-kafka-mapping': {
            id: `${kafkaIntegrationType}-kafka__mqtt-to-kafka`,
            'cluster-id': kafkaExtensionClusterID,
          },
        },
      ]
    }
  }

  return updatedConfig
}

/**
 * Accept unidirectional mapping:
 * - if mqtt-to-kafka-mapping's kafka-topic and mqtt-topic-filter are empty,
 *   make mqtt-to-kafka-mappings an empty array
 * - if kafka-to-mqtt-mapping's kafka-topic and mqtt-topic are empty, make
 *   kafka-to-mqtt-mappings an empty array
 * This function also adds missing IDs if needed.
 * @param {KafkaExtensionConfigVariant} json JSON Kafka configuration in the weird format xsd-forms is giving us
 * @return {KafkaExtensionConfiguration} cleaned-up Kafka configuration
 */
export function cleanupKafkaConfiguration(
  config: KafkaExtensionConfigVariant,
  flavor?: KafkaFlavor,
): KafkaExtensionConfiguration {
  const configVariant = config['kafka-configuration']

  const kafkaExtensionClusterID =
    configVariant?.['kafka-clusters']?.['kafka-cluster']?.id
  const kafkaIntegrationType =
    flavor ??
    (kafkaExtensionClusterID?.startsWith(KafkaFlavor.CONFLUENT)
      ? KafkaFlavor.CONFLUENT
      : KafkaFlavor.GENERIC)

  // Set cluster ID if it's not already set
  // If the cluster ID is not set, we need to set it based on the flavor
  let updatedClusterId = kafkaExtensionClusterID
  if (!updatedClusterId) {
    updatedClusterId =
      flavor === KafkaFlavor.CONFLUENT
        ? KafkaClusterId.CONFLUENT
        : KafkaClusterId.GENERIC
  }
  // If the flavor is provided, and the cluster ID is set, we need to check if
  // it matches the flavor
  if (flavor) {
    const isConfluentId = kafkaExtensionClusterID === KafkaClusterId.CONFLUENT
    const isGenericId = kafkaExtensionClusterID === KafkaClusterId.GENERIC

    if (isConfluentId && flavor === KafkaFlavor.GENERIC) {
      updatedClusterId = KafkaClusterId.GENERIC
    } else if (isGenericId && flavor === KafkaFlavor.CONFLUENT) {
      updatedClusterId = KafkaClusterId.CONFLUENT
    }

    // Update the cluster ID in the config if needed
    if (
      updatedClusterId !== kafkaExtensionClusterID &&
      configVariant?.['kafka-clusters']?.['kafka-cluster']
    ) {
      configVariant['kafka-clusters']['kafka-cluster'].id = updatedClusterId
    }
  }

  const mqttToKafkaMapping =
    configVariant?.['mqtt-to-kafka-mappings']?.['mqtt-to-kafka-mapping']
  const kafkaToMqttMapping =
    configVariant?.['kafka-to-mqtt-mappings']?.['kafka-to-mqtt-mapping']

  // Determine if mqtt-to-kafka-mapping should be included
  const mqttToKafkaTopic = (mqttToKafkaMapping?.['kafka-topic'] ?? '').trim()
  const mqttFilter = (
    mqttToKafkaMapping?.['mqtt-topic-filters']?.['mqtt-topic-filter'] ?? ''
  ).trim()
  const includeMqttToKafka =
    !!mqttToKafkaMapping && mqttToKafkaTopic.length > 0 && mqttFilter.length > 0

  // Determine if kafka-to-mqtt-mapping should be included
  const kafkaToMqttTopic = (
    kafkaToMqttMapping?.['kafka-topics']?.['kafka-topic'] ?? ''
  ).trim()
  const mqttTopic = (
    kafkaToMqttMapping?.['mqtt-topics']?.['mqtt-topic'] ?? ''
  ).trim()
  const includeKafkaToMqtt =
    !!kafkaToMqttMapping && kafkaToMqttTopic.length > 0 && mqttTopic.length > 0

  // Re-build the new config with the right mappings
  const {
    // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
    ['kafka-clusters']: kafkaCluster,
    // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
    ['mqtt-to-kafka-mappings']: _omitMqttToKafka,
    // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
    ['kafka-to-mqtt-mappings']: _omitKafkaToMqtt,
    ...rest
  } = configVariant ?? {}

  const newConfig: NonNullable<
    KafkaExtensionConfiguration['kafka-configuration']
  > = {
    ...rest, // everything except the mappings
  }

  if (kafkaCluster) {
    newConfig['kafka-clusters'] = [kafkaCluster]
  }

  // if id and cluster-id are defined but not aligned with the chosen flavor, update them
  function updateMappingId(id: string | undefined) {
    if (
      id?.startsWith(KafkaFlavor.GENERIC) &&
      flavor === KafkaFlavor.CONFLUENT
    ) {
      return undefined
    }

    if (
      id?.startsWith(KafkaFlavor.CONFLUENT) &&
      flavor === KafkaFlavor.GENERIC
    ) {
      return undefined
    }

    return id
  }

  if (includeMqttToKafka) {
    const id = updateMappingId(mqttToKafkaMapping.id)

    newConfig['mqtt-to-kafka-mappings'] = [
      {
        'mqtt-to-kafka-mapping': {
          ...mqttToKafkaMapping,
          id: id ?? `${kafkaIntegrationType}-kafka__mqtt-to-kafka`,
          'cluster-id': updatedClusterId,
          'kafka-topic': mqttToKafkaTopic,
          'mqtt-topic-filters': [
            {
              'mqtt-topic-filter': mqttFilter,
            },
          ],
        },
      },
    ]
  } else {
    newConfig['mqtt-to-kafka-mappings'] = []
  }

  if (includeKafkaToMqtt) {
    const id = updateMappingId(kafkaToMqttMapping.id)

    newConfig['kafka-to-mqtt-mappings'] = [
      {
        'kafka-to-mqtt-mapping': {
          ...kafkaToMqttMapping,
          id: id ?? `${kafkaIntegrationType}-kafka__kafka-to-mqtt`,
          'cluster-id': updatedClusterId,
          'kafka-topics': [
            {
              'kafka-topic': kafkaToMqttTopic,
            },
          ],
          'mqtt-topics': [
            {
              'mqtt-topic': mqttTopic,
            },
          ],
        },
      },
    ]
  } else {
    newConfig['kafka-to-mqtt-mappings'] = []
  }

  return {
    // biome-ignore lint/complexity/useLiteralKeys: cannot simplify here
    ['kafka-configuration']: newConfig,
  }
}
